import './styles.css'

import { FC, useCallback, useEffect, useRef, useState } from 'react'

import { Button, Flex, HStack, Icon, Input, Popover, Portal, Square, Text, useDisclosure } from '@chakra-ui/react'
import { ConfirmIcon, EditBoxIcon } from '@editor/assets/icons'
import { useOutsideClick } from '@infraloop/common'

const PopoverEditing: FC<{
  titlePostfix?: string
  allowEmpty?: boolean
  value: string
  defaultValue?: string
  onChange: (value: string) => void
}> = ({ titlePostfix, allowEmpty = true, value, defaultValue, onChange }) => {
  const { onOpen, onClose, open } = useDisclosure()

  // State
  const [originalValue, setOriginalValue] = useState(value)
  const [editedValue, setEditedValue] = useState(value || defaultValue)

  // Ref
  const nameEditRef = useRef<HTMLInputElement>(null)

  /**
   * Try to focus the input element.
   */
  const tryFocus = useCallback(() => {
    if (nameEditRef.current) {
      nameEditRef.current.focus()
      return
    }

    setTimeout(() => tryFocus(), 100)
  }, [nameEditRef])

  const reset = useCallback(() => {
    setEditedValue(originalValue || defaultValue)
    onChange(originalValue || defaultValue || '')
  }, [originalValue, onChange])

  useEffect(() => {
    setOriginalValue(value)
  }, [value])

  /**
   * Handle outside click.
   */
  useOutsideClick({
    ref: nameEditRef,
    handler: () => {
      if (!allowEmpty && !editedValue) {
        reset()
      }
      onClose()
    },
  })

  return (
    <Flex gap={2} className="popover-editing">
      <Popover.Root
        closeOnEscape
        closeOnInteractOutside
        positioning={{ placement: 'top-start' }}
        open={open}
        onOpenChange={(isOpen) => {
          if (isOpen) {
            tryFocus()
            onOpen()
          } else {
            onClose()
          }
        }}
      >
        <Popover.Trigger>
          <Square>
            <Icon as={EditBoxIcon} boxSize={5} />
          </Square>
        </Popover.Trigger>
        <Portal>
          <Popover.Positioner>
            <Popover.Content>
              <Popover.Arrow />
              <Popover.Body>
                <HStack>
                  <Input
                    ref={nameEditRef}
                    defaultValue={value}
                    placeholder={defaultValue}
                    onChange={(event) => {
                      if (!allowEmpty && !event.target.value) {
                        return
                      }

                      event.preventDefault()
                      event.stopPropagation()
                      setEditedValue(event.target.value || defaultValue)
                      onChange(event.target.value)
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') {
                        if (!allowEmpty && !editedValue) {
                          reset()
                        }
                        onClose()
                      }
                    }}
                  />
                  <Button colorPalette="blue" onClick={onClose}>
                    <Icon as={ConfirmIcon} boxSize={5} />
                  </Button>
                </HStack>
              </Popover.Body>
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
      <Text>
        {editedValue} {titlePostfix}
      </Text>
    </Flex>
  )
}

export default PopoverEditing
